import { useEffect, useState } from "react";

const CURRENT_VERSION = (import.meta.env.VITE_BUILD_VERSION as string | undefined) ?? "dev";
const POLL_INTERVAL_MS = 60_000;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data?.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Polls /version.json and returns true when the deployed version differs
 * from the version this client was built with.
 */
export function useAppVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Skip in dev / preview iframes
    if (!import.meta.env.PROD) return;
    if (CURRENT_VERSION === "dev") return;

    let cancelled = false;
    let timer: number | undefined;

    const check = async () => {
      const remote = await fetchRemoteVersion();
      if (cancelled) return;
      if (remote && remote !== CURRENT_VERSION) {
        setUpdateAvailable(true);
      }
    };

    const schedule = () => {
      timer = window.setTimeout(async () => {
        await check();
        if (!cancelled) schedule();
      }, POLL_INTERVAL_MS);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };

    void check();
    schedule();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  return updateAvailable;
}
